// apps/web/src/api/cargo-payment-docs.ts — typed wrappers around the Cargo
// client-payment DOCUMENT endpoints (#1232). Kept separate from the registry
// write surface so the payment-modal feature owns a cohesive client. The raw
// upload goes through the shared storage presign + PUT; this module only binds
// the already-uploaded object and lists/downloads/deletes the bound rows.

import {
  CargoPaymentDocumentListResponseSchema,
  CargoPaymentDocumentWireSchema,
  StorageDownloadUrlResponseSchema,
  type CargoPaymentDocumentCreateInput,
  type CargoPaymentDocumentListResponse,
  type CargoPaymentDocumentWire,
  type StorageDownloadUrlResponse,
} from '@magimp/shared';
import { z } from 'zod';

import { request } from './client.js';

const NoContentSchema = z.void();

export function listCargoPaymentDocuments(
  cargoId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CargoPaymentDocumentListResponse> {
  return request<CargoPaymentDocumentListResponse>(
    `/cargo/${encodeURIComponent(cargoId)}/payment-documents`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoPaymentDocumentListResponseSchema,
  );
}

export function createCargoPaymentDocument(
  cargoId: string,
  input: CargoPaymentDocumentCreateInput,
): Promise<CargoPaymentDocumentWire> {
  return request<CargoPaymentDocumentWire>(
    `/cargo/${encodeURIComponent(cargoId)}/payment-documents`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoPaymentDocumentWireSchema,
  );
}

// POST with an empty body → a short-lived presigned download URL the caller
// opens in a new tab. Never expose a public/object URL directly.
export function cargoPaymentDocumentDownloadUrl(
  cargoId: string,
  documentId: string,
): Promise<StorageDownloadUrlResponse> {
  return request<StorageDownloadUrlResponse>(
    `/cargo/${encodeURIComponent(cargoId)}/payment-documents/${encodeURIComponent(
      documentId,
    )}/download-url`,
    { method: 'POST' },
    {},
    StorageDownloadUrlResponseSchema,
  );
}

export function deleteCargoPaymentDocument(cargoId: string, documentId: string): Promise<void> {
  return request<void>(
    `/cargo/${encodeURIComponent(cargoId)}/payment-documents/${encodeURIComponent(documentId)}`,
    { method: 'DELETE' },
    {},
    NoContentSchema,
  );
}
